import ArrowSVG from '@public/icons/btn-arr.svg';
import classNames from 'classnames';
import { MutableRefObject, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Document } from '@/entities/document';
import Pagination from '@/shared/components/shared/Pagination';
import Typografed from '@/shared/components/shared/Typografed';
import { isPdf } from '@/shared/hooks/use-form-sender/validator/utils';
import { useMounted } from '@/shared/hooks/use-mounted';
import { useOverflowing } from '@/shared/hooks/use-overflowing';
import { tp } from '@/shared/lib/typograf';

export type ExpoDocumentsSectionProps = {
    title: string;
    data: Document[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    expoDocumentsSectionProps: ExpoDocumentsSectionProps;
};

const PREV_CLASS = 'expo-documents-slider-prev-btn';
const NEXT_CLASS = 'expo-documents-slider-next-btn';

function ExpoDocumentsSection({ expoDocumentsSectionProps, ...props }: Props) {
    const sliderRef = useRef<SwiperRef>();
    const [lastRealIndex, setLastRealIndex] = useState(0);
    const [currentRealIndex, setCurrentRealIndex] = useState(0);
    const documents = expoDocumentsSectionProps.data;
    const title = expoDocumentsSectionProps.title;
    const numSlides = documents.length;

    const slider = useRef<HTMLDivElement | null>(null);
    const isInitialized = useOverflowing(slider);
    const isMounted = useMounted();

    if (numSlides === 0) {
        return null;
    }

    return (
        <section {...props} className={classNames('section__documents expo-documents-section', props.className)}>
            <div className="documents__wrapper">
                <h2 className="documents__title h3 wrapper">{title}</h2>
                <div className="expo-documents-bottom">
                    {isInitialized && isMounted ? (
                        <>
                            <Swiper
                                ref={sliderRef as MutableRefObject<SwiperRef>}
                                className={classNames('expo-documents-slider', { 'is-enabled': isInitialized })}
                                modules={[Navigation]}
                                navigation={{
                                    prevEl: `.${PREV_CLASS}`,
                                    nextEl: `.${NEXT_CLASS}`,
                                }}
                                speed={650}
                                slidesPerView={'auto'}
                                spaceBetween={20}
                                breakpoints={{
                                    320: {
                                        slidesPerView: 1.2,
                                        spaceBetween: 8,
                                        centeredSlides: true,
                                    },
                                    767: {
                                        spaceBetween: 12,
                                        slidesOffsetBefore: 22,
                                        slidesOffsetAfter: 22,
                                    },
                                    1198: {
                                        slidesOffsetBefore: 36,
                                        slidesOffsetAfter: 36,
                                        spaceBetween: 20,
                                    },
                                    1366: {
                                        spaceBetween: 20,
                                        slidesOffsetBefore: 70,
                                        slidesOffsetAfter: 70,
                                    },
                                }}
                                onSlideChange={(swiper) => {
                                    const newRealIndex = swiper.realIndex;
                                    setLastRealIndex(currentRealIndex);
                                    setCurrentRealIndex(newRealIndex);
                                }}
                            >
                                {documents.map((doc, i) => (
                                    <SwiperSlide
                                        className="expo-documents-slide-card expo-documents-slider-slide"
                                        key={`${doc.src}-${i}`}
                                    >
                                        <div className="expo-documents-slide-text">
                                            <div
                                                className="expo-documents-slide-title text-xl"
                                                dangerouslySetInnerHTML={{ __html: tp(doc.title) }}
                                            />
                                        </div>
                                        <div className="expo-documents-slide-btn-wrapper">
                                            <a
                                                href={doc.src}
                                                target={isPdf(doc.src) ? '_blank' : undefined}
                                                rel={isPdf(doc.src) ? 'noreferrer noopener' : undefined}
                                                download={isPdf(doc.src) ? undefined : true}
                                                className="btn btn-outline-dark btn-geometry-outline-dark expo-documents-slide-btn"
                                            >
                                                <ArrowSVG className="btn__arrow" />
                                                <span className="btn__text">Скачать файл</span>
                                            </a>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                            <Pagination
                                numSlides={numSlides}
                                currentRealIndex={currentRealIndex}
                                lastRealIndex={lastRealIndex}
                                prevElClass={PREV_CLASS}
                                nextElClass={NEXT_CLASS}
                                wrapperClass="expo-documents-pagination-wrapper wrapper"
                                hidden={!isInitialized}
                            />
                        </>
                    ) : (
                        <div className="expo-documents-slider-fallback swiper__wrapper wrapper" ref={slider}>
                            <div className="expo-documents-list">
                                {documents.map((doc, i) => (
                                    <div
                                        className="expo-documents-slide-card expo-documents-slider-slide"
                                        key={`${doc.src}-${i}`}
                                    >
                                        <div className="expo-documents-slide-text">
                                            <div className="expo-documents-slide-title text-xl">
                                                <Typografed>{doc.title}</Typografed>
                                            </div>
                                        </div>
                                        <div className="expo-documents-slide-btn-wrapper">
                                            <a
                                                href={doc.src}
                                                target={isPdf(doc.src) ? '_blank' : undefined}
                                                rel={isPdf(doc.src) ? 'noreferrer noopener' : undefined}
                                                download={isPdf(doc.src) ? undefined : true}
                                                className="btn btn-outline-dark btn-geometry-outline-dark expo-documents-slide-btn"
                                            >
                                                <ArrowSVG className="btn__arrow" />
                                                <span className="btn__text">Скачать файл</span>
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default ExpoDocumentsSection;
